import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card, Input, Table, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { differenceInBusinessDays, differenceInCalendarDays, parseISO } from 'date-fns'
import { useIntl } from 'react-intl'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { getFormattedDaysAndHours } from '@vacationtracker/shared/functions/get-days-or-hours'
import { IMember } from '@vacationtracker/shared/types/team-energy'
import { LeaveDisplayFormatEnum } from '@vacationtracker/shared/types/display-in-days-hours'
import { MINIMUM_BREAK_PTO_DAYS, OVERDUE_PTO_DAYS } from '@vacationtracker/shared/functions/user-insights/team-energy-rules'
import EnergyBar from './EnergyBar'
import Sparkline from './Sparkline'
import StatusPill from './StatusPill'
import MemberDetailDrawer from './MemberDetailDrawer'
import { formatShortDate } from '../utils/formatShortDate'
import { trackMemberClicked, trackPageChanged, trackSearchUsed } from '../services/teamEnergyTracking'
import useDebounce from '../../../util/use-debounce'

interface IMembersTableProps {
  members: IMember[]
  departmentName: string | undefined
  pageSize?: number
}

const DEFAULT_PAGE_SIZE = 10

function getDaysOff(pto: { startDate: string; endDate: string }): number {
  return differenceInBusinessDays(parseISO(pto.endDate), parseISO(pto.startDate)) + 1
}

const MembersTable: React.FC<IMembersTableProps> = ({ members, departmentName, pageSize = DEFAULT_PAGE_SIZE }) => {
  const { formatMessage, locale } = useIntl()
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedMember, setSelectedMember] = useState<IMember | null>(null)
  const debouncedSearch = useDebounce(search, 500)
  const lastTrackedSearchRef = useRef<string | null>(null)

  useEffect(() => {
    if (lastTrackedSearchRef.current === null && debouncedSearch === '') {
      lastTrackedSearchRef.current = ''
      return
    }
    if (lastTrackedSearchRef.current === debouncedSearch) {
      return
    }
    lastTrackedSearchRef.current = debouncedSearch
    trackSearchUsed({ queryLength: debouncedSearch.trim().length })
  }, [debouncedSearch])

  const filtered = useMemo(() => {
    const trimmed = search.trim().toLowerCase()
    if (!trimmed) {
      return members
    }
    return members.filter((m) => (m.name ?? '').toLowerCase().includes(trimmed))
  }, [members, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)

  // Reset to page 1 when the user types in the search box.
  useEffect(() => {
    setCurrentPage(1)
  }, [search])

  const columns: ColumnsType<IMember> = useMemo(
    () => [
      {
        title: <IntlMessages id="teamEnergy.members.name" />,
        dataIndex: 'name',
        key: 'name',
        render: (_value, member) => (
          <div className="te-name-cell">
            <UserAvatar id={member.userId} avatar={member.imageUrl ?? undefined} name={member.name ?? member.userId} shape="circle" />
            <div className="te-name-cell__name">{member.name ?? member.userId}</div>
          </div>
        ),
      },
      {
        title: <IntlMessages id="app.energy" />,
        key: 'energy',
        render: (_value, member) =>
          member.isOptedOut || !member.energy ? <span className="te-empty-cell">-</span> : <EnergyBar score={member.energy.score} mode={member.energy.mode} />,
      },
      {
        title: <IntlMessages id="teamEnergy.members.status" />,
        key: 'status',
        render: (_value, member) => (member.status ? <StatusPill status={member.status} /> : <span className="te-empty-cell">-</span>),
      },
      {
        title: <IntlMessages id="teamEnergy.members.trend" />,
        key: 'trend',
        render: (_value, member) =>
          member.isOptedOut || !member.weeklyScores || member.weeklyScores.length < 2 ? (
            <span className="te-empty-cell">-</span>
          ) : (
            <Sparkline scores={member.weeklyScores} trendDirection={member.trendDirection} />
          ),
      },
      {
        title: (
          <>
            <IntlMessages id="teamEnergy.members.lastPto" />{' '}
            <Tooltip title={<IntlMessages id="teamEnergy.members.lastPtoTooltip" values={{ minDays: MINIMUM_BREAK_PTO_DAYS }} />}>
              <QuestionCircleOutlined style={{ cursor: 'pointer' }} />
            </Tooltip>
          </>
        ),
        key: 'lastPto',
        render: (_value, member) => {
          const lastPto = member.lastPto
          if (member.isOptedOut || !lastPto) {
            return <span className="te-empty-cell">-</span>
          }
          const rawDiff = differenceInCalendarDays(new Date(), new Date(lastPto.endDate))
          const isOngoing = rawDiff <= 0
          const daysAgo = Math.max(0, rawDiff)
          const isOverdue = daysAgo >= OVERDUE_PTO_DAYS
          const daysOff = getDaysOff(lastPto)
          const tooltipTitle = (
            <div>
              <div>
                {formatMessage(
                  { id: 'teamEnergy.members.lastPtoRange' },
                  {
                    startDate: formatShortDate(lastPto.startDate, locale),
                    endDate: formatShortDate(lastPto.endDate, locale),
                  }
                )}
              </div>
              {lastPto.holidays > 0 && <div>{formatMessage({ id: 'teamEnergy.members.ptoBreakdown' }, { ptoDays: lastPto.workingDays, holidays: lastPto.holidays })}</div>}
            </div>
          )
          const content = (
            <div className="te-pto-cell">
              <div className={isOverdue ? 'te-overdue-text te-overdue-text--overdue' : 'te-overdue-text'}>
                {isOngoing
                  ? formatMessage({ id: 'teamEnergy.members.until' }, { date: formatShortDate(lastPto.endDate, locale) })
                  : formatMessage({ id: 'teamEnergy.members.daysAgo' }, { days: daysAgo })}
              </div>
              <div className="te-cell-secondary">{formatMessage({ id: 'teamEnergy.members.daysOff' }, { days: daysOff })}</div>
            </div>
          )
          return <Tooltip title={tooltipTitle}>{content}</Tooltip>
        },
      },
      {
        title: (
          <>
            <IntlMessages id="teamEnergy.members.nextPto" />{' '}
            <Tooltip title={<IntlMessages id="teamEnergy.members.nextPtoTooltip" values={{ minDays: MINIMUM_BREAK_PTO_DAYS }} />}>
              <QuestionCircleOutlined style={{ cursor: 'pointer' }} />
            </Tooltip>
          </>
        ),
        key: 'nextPto',
        render: (_value, member) => {
          if (member.isOptedOut) {
            return <span className="te-empty-cell">-</span>
          }
          const nextPto = member.nextPto
          if (!nextPto) {
            return <span className="te-empty-cell">-</span>
          }
          const daysOff = getDaysOff(nextPto)
          const tooltipTitle = (
            <div>
              <div>
                {formatMessage(
                  { id: 'teamEnergy.members.lastPtoRange' },
                  {
                    startDate: formatShortDate(nextPto.startDate, locale),
                    endDate: formatShortDate(nextPto.endDate, locale),
                  }
                )}
              </div>
              {nextPto.holidays > 0 && <div>{formatMessage({ id: 'teamEnergy.members.ptoBreakdown' }, { ptoDays: nextPto.workingDays, holidays: nextPto.holidays })}</div>}
            </div>
          )
          const content = (
            <div className="te-pto-cell">
              <div className="te-next-pto-date">{formatShortDate(nextPto.startDate, locale)}</div>
              <div className="te-cell-secondary">{formatMessage({ id: 'teamEnergy.members.daysOff' }, { days: daysOff })}</div>
            </div>
          )
          return <Tooltip title={tooltipTitle}>{content}</Tooltip>
        },
      },
      {
        title: <IntlMessages id="teamEnergy.members.remainingBalance" />,
        key: 'remainingBalance',
        render: (_value, member) => {
          const balance = member.remainingBalance
          if (member.isOptedOut || balance === null) {
            return <span className="te-empty-cell">-</span>
          }
          return (
            <span>
              {getFormattedDaysAndHours({
                leave: balance,
                formatMessage,
                displayFormat: LeaveDisplayFormatEnum.short,
              })}
            </span>
          )
        },
      },
    ],
    [formatMessage, locale]
  )

  return (
    <Card size="small" className="te-members">
      <div className="te-members__search-row">
        <Input.Search
          allowClear
          className="te-members__search"
          placeholder={formatMessage({ id: 'teamEnergy.members.searchPlaceholder' })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <Table<IMember>
        columns={columns}
        dataSource={filtered}
        rowKey="userId"
        pagination={{
          current: safePage,
          pageSize,
          total: filtered.length,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: (page) => {
            trackPageChanged({ page, pageSize })
            setCurrentPage(page)
          },
        }}
        locale={{ emptyText: formatMessage({ id: 'teamEnergy.members.empty' }) }}
        scroll={{ x: 'max-content' }}
        rowClassName="te-member-row--clickable"
        onRow={(member) => ({
          onClick: () => {
            trackMemberClicked({ userId: member.userId, status: member.status ?? 'unknown' })
            setSelectedMember(member)
          },
        })}
      />
      <MemberDetailDrawer member={selectedMember} departmentName={departmentName} onClose={() => setSelectedMember(null)} />
    </Card>
  )
}

export default MembersTable
